import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Spin, Tag, Collapse, Typography } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, SearchOutlined, DatabaseOutlined } from '@ant-design/icons';
import './ChatMCPAssistant.css';

const { TextArea } = Input;
const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

/**
 * 基于ChatMessage的真正MCP助手
 * 专门用于智能查询和分析聊天记录数据
 */
const ChatMCPAssistant = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: '👋 你好！我是聊天记录智能助手。我可以帮你：\n\n🔍 **智能搜索聊天记录**\n• "查找我们讨论过压力的对话"\n• "找到最近关于工作的聊天"\n• "搜索包含\'焦虑\'的消息"\n\n📊 **分析聊天模式**\n• "分析我最近的聊天主题"\n• "我通常什么时候最活跃？"\n• "统计我的情感表达模式"\n\n💡 **智能洞察**\n• "从聊天记录中提取关键洞察"\n• "分析我的沟通风格变化"\n• "总结重要的对话要点"',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 调用真正的MCP API来查询ChatMessage数据
  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // 调用MCP工具API
      const response = await fetch('/api/mcp/tools/call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: "query_chat_records",
          arguments: {
            userId: "6",
            query: inputText,
            limit: 50,
            includeAnalysis: true,
            includeContext: true
          }
        })
      });

      if (!response.ok) {
        throw new Error('MCP查询失败');
      }

      const data = await response.json();
      
      // 解析MCP响应
      let responseContent = "查询完成，但没有找到相关结果。";
      let mcpResults = [];
      let analysis = null;
      let resultCount = 0;
      
      if (!data.isError && data.content && data.content.length > 0) {
        const content = data.content[0];
        if (content.type === 'text') {
          try {
            const mcpData = JSON.parse(content.text);
            responseContent = mcpData.aiAnalysis || mcpData.message || "查询完成";
            mcpResults = mcpData.messages || [];
            analysis = mcpData.analysis || null;
            resultCount = mcpData.messageCount || 0;
          } catch (e) {
            responseContent = content.text;
          }
        }
      } else if (data.isError) {
        responseContent = `查询失败：${data.errorMessage || "未知错误"}`;
      }
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: responseContent,
        timestamp: new Date(),
        mcpResults: mcpResults,
        analysis: analysis,
        queryInfo: {
          tool: 'query_chat_records',
          searchQuery: inputText,
          resultCount: resultCount,
          searchParams: { query: inputText, limit: 50 }
        }
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('MCP查询失败:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: '抱歉，查询聊天记录时出现问题。请稍后再试，或者换个方式提问。',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // 渲染MCP查询结果
  const renderMCPResults = (message) => {
    if (!message.mcpResults || message.mcpResults.length === 0) return null;

    return (
      <div className="mcp-results">
        <div className="mcp-header">
          <DatabaseOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          <Text strong>MCP查询结果</Text>
          {message.queryInfo && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {message.queryInfo.resultCount} 条结果
            </Tag>
          )}
        </div>
        
        <Collapse size="small" style={{ marginTop: 12 }}>
          <Panel 
            header={`查看详细结果 (${message.mcpResults.length}条)`} 
            key="results"
            extra={<SearchOutlined />}
          >
            <div className="chat-results">
              {message.mcpResults.slice(0, 10).map((result, index) => (
                <div key={index} className="chat-result-item">
                  <div className="result-header">
                    <div className="message-type">
                      {result.type === 'user' ? (
                        <Tag color="blue" icon={<UserOutlined />}>用户</Tag>
                      ) : (
                        <Tag color="purple" icon={<RobotOutlined />}>AI</Tag>
                      )}
                    </div>
                    <div className="message-time">
                      {new Date(result.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className="result-content">
                    <Paragraph ellipsis={{ rows: 2, expandable: true }}>
                      {result.content}
                    </Paragraph>
                  </div>
                  {result.session && (
                    <div className="result-meta">
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        会话: {result.session}
                      </Text>
                    </div>
                  )}
                </div>
              ))}
              {message.mcpResults.length > 10 && (
                <div className="more-results">
                  <Text type="secondary">
                    还有 {message.mcpResults.length - 10} 条结果未显示...
                  </Text>
                </div>
              )}
            </div>
          </Panel>
        </Collapse>

        {message.analysis && (
          <Collapse size="small" style={{ marginTop: 8 }}>
            <Panel header="智能分析" key="analysis" extra={<RobotOutlined />}>
              <div className="mcp-analysis">
                {Object.entries(message.analysis).map(([key, value]) => (
                  <div key={key} className="analysis-item">
                    <Text strong>{formatAnalysisKey(key)}: </Text>
                    <Text>{typeof value === 'object' ? JSON.stringify(value) : value}</Text>
                  </div>
                ))}
              </div>
            </Panel>
          </Collapse>
        )}

        {message.queryInfo && (
          <div className="query-info">
            <Text type="secondary" style={{ fontSize: 12 }}>
              🔍 使用MCP工具: {message.queryInfo.tool} | 
              查询: "{message.queryInfo.searchQuery}" | 
              结果: {message.queryInfo.resultCount} 条
            </Text>
          </div>
        )}
      </div>
    );
  };

  // 格式化分析键名
  const formatAnalysisKey = (key) => {
    const keyMap = {
      'totalMessages': '总消息数',
      'timeRange': '时间范围',
      'participants': '参与者',
      'topics': '主要话题',
      'sentiment': '情感倾向',
      'keyInsights': '关键洞察',
      'patterns': '对话模式'
    };
    return keyMap[key] || key;
  };

  // 格式化消息内容
  const formatMessage = (content) => {
    return content.split('\n').map((line, index) => (
      <div key={index}>
        {line}
        {index < content.split('\n').length - 1 && <br />}
      </div>
    ));
  };

  // 快速查询建议
  const quickQueries = [
    "查找最近一周的聊天记录",
    "搜索包含工作的对话",
    "分析我的聊天情感趋势",
    "找到关于健康的讨论",
    "统计最活跃的聊天时间",
    "提取重要的决定和计划"
  ];

  return (
    <div className="chat-mcp-assistant">
      <div className="chat-header">
        <div className="header-info">
          <h2>💬 聊天记录MCP助手</h2>
          <p>使用MCP协议智能查询和分析你的聊天记录数据</p>
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
              <div className="message-text">
                {formatMessage(message.content)}
              </div>
              
              {/* 渲染MCP查询结果 */}
              {message.mcpResults && renderMCPResults(message)}
              
              <div className="message-time">
                {message.timestamp.toLocaleTimeString()}
                {message.isError && (
                  <Tag color="red" style={{ marginLeft: 8 }}>错误</Tag>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message ai">
            <div className="message-content">
              <div className="loading-message">
                <Spin size="small" style={{ marginRight: 8 }} />
                正在通过MCP查询聊天记录...
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* 快速查询建议 */}
      <div className="quick-queries">
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          💡 试试这些查询:
        </Text>
        <div className="query-tags">
          {quickQueries.map((query, index) => (
            <Tag
              key={index}
              color="blue"
              style={{ cursor: 'pointer', marginBottom: 4 }}
              onClick={() => setInputText(query)}
            >
              {query}
            </Tag>
          ))}
        </div>
      </div>

      {/* 输入区域 */}
      <div className="chat-input">
        <div className="input-wrapper">
          <TextArea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="输入你想查询的内容，例如：\'查找我们讨论过压力的对话\'..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={isLoading}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            loading={isLoading}
            disabled={!inputText.trim()}
            style={{ marginLeft: 8 }}
          >
            查询
          </Button>
        </div>
        <div className="input-hint">
          <Text type="secondary" style={{ fontSize: 12 }}>
            💡 支持自然语言查询 | 按Enter发送 | 基于MCP协议的智能搜索
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ChatMCPAssistant;